"use client";

import { type RefObject, useEffect, useRef } from "react";
import type { AnalyticsEventName } from "@/lib/analytics/events";
import { trackEvent } from "@/lib/analytics/track";

type TrackableProp = string | number | boolean | null | undefined;

export function useElementViewTracking<T extends Element>({
  ref,
  eventName,
  props,
  threshold = 0.35,
}: {
  ref: RefObject<T | null>;
  eventName: AnalyticsEventName;
  props?: Record<string, TrackableProp>;
  threshold?: number;
}) {
  const propsRef = useRef(props);
  const trackedRef = useRef(false);

  useEffect(() => {
    propsRef.current = props;
  }, [props]);

  useEffect(() => {
    const element = ref.current;
    if (!element || trackedRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || entry.intersectionRatio < threshold) return;
        trackedRef.current = true;
        trackEvent(eventName, propsRef.current);
        observer.disconnect();
      },
      { threshold },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [eventName, ref, threshold]);
}
